/**
 * State Tuning — three states (Calm / Focus / Confidence) with breathing orb.
 *
 * Routes:
 *   /practices/state-tuning            → explainer
 *   /practices/state-tuning/select     → 3 cards (state selection)
 *   /practices/state-tuning/play/:id   → orb session
 *
 * Audio is generated client-side via Web Audio API (no files needed):
 *   calm        → 432 Hz pure sine + soft pad
 *   focus       → 200 Hz / 240 Hz binaural (40 Hz beat)
 *   confidence  → 528 Hz Solfeggio + warm fifth
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, X, Pause, Play } from "lucide-react";
import PracticeExplainer from "./PracticeExplainer";

/* ----------------------------- EXPLAINER ------------------------------ */
export const StateTuningIntro = () => (
  <PracticeExplainer
    testId="state-tuning-intro"
    eyebrow="State Tuning"
    title="Shift Your State"
    accent="rgba(190,205,235,0.6)"
    lines={[
      "Sound is a doorway.",
      "Tune your inner state with carefully chosen tones — chosen to bring you back to centre, sharpen your focus, or open you up.",
      "Choose a state. Press play. Let the tone do the work.",
    ]}
    cta="Choose A State"
    to="/practices/state-tuning/select"
  />
);

/* -------------------------- STATE SELECTION --------------------------- */
const STATES = [
  {
    id: "calm",
    title: "Calm",
    sub: "Return to centre",
    glow: "rgba(160,200,210,0.35)",
    accent: "rgba(180,220,225,0.6)",
    diffuse: 1.0,
  },
  {
    id: "focus",
    title: "Focus",
    sub: "Clear & direct",
    glow: "rgba(190,205,230,0.35)",
    accent: "rgba(210,225,245,0.6)",
    diffuse: 0.55,
  },
  {
    id: "confidence",
    title: "Confidence",
    sub: "Open & ready",
    glow: "rgba(230,190,150,0.35)",
    accent: "rgba(245,210,170,0.62)",
    diffuse: 1.25,
  },
];

const navyBg = {
  background: "radial-gradient(ellipse at 50% 22%, #0E1A2C 0%, #0A1424 32%, #060B16 68%, #03060D 100%)",
};

export const StateTuningSelect = () => {
  const navigate = useNavigate();
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setArrived(true), 100);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen relative overflow-hidden" style={navyBg} data-testid="state-tuning-select">
      <header className="relative z-10 px-5 pt-5 pb-2 flex items-center">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-white/[0.04] transition-colors" aria-label="Back" data-testid="state-tuning-select-back">
          <ArrowLeft size={20} className="text-white/55" />
        </button>
      </header>

      <main className="relative z-10 max-w-md mx-auto px-6 pt-6 pb-32">
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: arrived ? 1 : 0, scale: arrived ? 1 : 0.7 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mb-10 relative"
          style={{ width: 120, height: 120 }}
        >
          <BreathingOrb size={120} hue="warm" intensity={0.6} />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 8 }}
          transition={{ duration: 1.0, delay: 0.4 }}
          className="text-white/90 text-3xl text-center"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Choose your state
        </motion.h1>

        <div className="mt-12 space-y-5">
          {STATES.map((s, i) => (
            <motion.button
              key={s.id}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 18 }}
              transition={{ duration: 1.0, delay: 0.7 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ scale: 1.012 }}
              whileTap={{ scale: 0.985 }}
              onClick={() => navigate(`/practices/state-tuning/play/${s.id}`)}
              data-testid={`state-card-${s.id}`}
              className="w-full text-left rounded-3xl px-7 py-7 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(140deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.015) 60%, rgba(255,255,255,0.03) 100%)",
                backdropFilter: "blur(14px)",
                border: "1px solid rgba(255,255,255,0.06)",
                boxShadow: `0 22px 60px -28px ${s.glow}, inset 0 1px 0 rgba(255,255,255,0.03)`,
              }}
            >
              <motion.div
                aria-hidden
                animate={{ opacity: [0.45, 0.8, 0.45], scale: [1, 1.06, 1] }}
                transition={{ duration: 7 + i * 0.4, repeat: Infinity, ease: "easeInOut" }}
                className="pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full"
                style={{
                  background: `radial-gradient(circle, ${s.glow} 0%, transparent ${60 + s.diffuse * 10}%)`,
                  filter: `blur(${8 + s.diffuse * 4}px)`,
                }}
              />
              <div className="relative">
                <h3
                  className="text-white/95 text-[26px]"
                  style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400 }}
                >
                  {s.title}
                </h3>
                <p className="mt-1 text-white/50 text-sm font-light">{s.sub}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </main>
    </div>
  );
};

/* --------------------------- BREATHING ORB --------------------------- */
function BreathingOrb({ size = 220, hue = "warm", intensity = 1, audioPulse = 0 }) {
  const tints = {
    warm: { core: "rgba(255, 232, 200, 0.95)", glow: "rgba(225, 180, 140, 0.55)", outer: "rgba(180, 140, 110, 0.18)" },
    cool: { core: "rgba(220, 235, 250, 0.9)", glow: "rgba(170, 195, 225, 0.5)", outer: "rgba(120, 150, 190, 0.18)" },
    neutral: { core: "rgba(245, 240, 230, 0.9)", glow: "rgba(220, 210, 195, 0.45)", outer: "rgba(180, 170, 155, 0.16)" },
  };
  const t = tints[hue] || tints.warm;
  return (
    <motion.div
      animate={{ scale: [1, 1.08 + audioPulse * 0.06, 1], opacity: [0.85, 1, 0.85] }}
      transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      className="relative rounded-full"
      style={{ width: size, height: size }}
    >
      {/* Outer halo */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${t.glow} 0%, ${t.outer} 40%, transparent 72%)`,
          filter: `blur(${size * 0.05}px)`,
          transform: `scale(${1.4 * intensity})`,
        }}
      />
      {/* Core orb */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(circle at 45% 40%, ${t.core} 0%, ${t.glow} 38%, transparent 72%)`,
          boxShadow: `0 0 ${size * 0.4}px ${t.glow}`,
        }}
      />
    </motion.div>
  );
}

/* --------------------------- SESSION SCREEN -------------------------- */
// Reset Mode = a short, self-completing tune-up (timer visible, auto-ends).
// Stay On    = an open-ended ambient field that evolves; no auto-end.
const TONE_RECIPES = {
  calm: {
    name: "Calm", hue: "cool",
    text: ["Just breathe", "Let everything settle", "Stay here", "Soften", "Release the day"],
    stayOnText: ["Sink deeper", "Nothing to hold", "Rest here", "You are held", "Let it go"],
    freqs: [{ left: 432, right: 432 }, { left: 216, right: 216, gain: 0.18 }],
    resetDuration: 240, // 4 min
  },
  focus: {
    name: "Focus", hue: "neutral",
    text: ["Clear", "Sharp", "Right here", "One thing", "Present"],
    stayOnText: ["One-pointed", "Absorbed", "In the work", "Nothing else", "Flow"],
    freqs: [{ left: 200, right: 240 }, { left: 100, right: 100, gain: 0.12 }],
    resetDuration: 180, // 3 min
  },
  confidence: {
    name: "Confidence", hue: "warm",
    text: ["Open", "Ready", "You are enough", "Here, now"],
    stayOnText: ["Expand", "Chosen", "Grounded", "Certain", "Already yours"],
    freqs: [{ left: 528, right: 528 }, { left: 396, right: 396, gain: 0.15 }],
    resetDuration: 180, // 3 min
  },
};

const WHISPERS_FALLBACK = ["Just breathe", "Stay here", "Soften"];

export const StateTuningPlay = () => {
  const { stateId } = useParams();
  const navigate = useNavigate();
  const recipe = TONE_RECIPES[stateId] || TONE_RECIPES.calm;
  const [playing, setPlaying] = useState(false);
  const [resetMode, setResetMode] = useState(true);
  const [whisperIdx, setWhisperIdx] = useState(0);
  const [whisperVisible, setWhisperVisible] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const audioCtxRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const masterGainRef = useRef(null);

  const startAudio = () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();

      const master = ctx.createGain();
      master.gain.setValueAtTime(0, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 1.6); // gentle fade-in
      master.connect(ctx.destination);
      masterGainRef.current = master;

      const merger = ctx.createChannelMerger(2);
      merger.connect(master);

      const oscs = [];
      recipe.freqs.forEach((pair) => {
        const oscL = ctx.createOscillator();
        oscL.type = "sine";
        oscL.frequency.value = pair.left;
        const gL = ctx.createGain();
        gL.gain.value = pair.gain ?? 0.5;
        oscL.connect(gL);
        gL.connect(merger, 0, 0);
        oscL.start();

        const oscR = ctx.createOscillator();
        oscR.type = "sine";
        oscR.frequency.value = pair.right;
        const gR = ctx.createGain();
        gR.gain.value = pair.gain ?? 0.5;
        oscR.connect(gR);
        gR.connect(merger, 0, 1);
        oscR.start();

        oscs.push(oscL, oscR);
      });
      oscillatorsRef.current = oscs;
      setPlaying(true);
    } catch (e) {
      console.error("Audio start failed", e);
    }
  };

  const stopAudio = (fade = true) => {
    try {
      const ctx = audioCtxRef.current;
      const master = masterGainRef.current;
      if (ctx && master && fade) {
        master.gain.cancelScheduledValues(ctx.currentTime);
        master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
        master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.9);
      }
      setTimeout(() => {
        oscillatorsRef.current.forEach((o) => {
          try { o.stop(); o.disconnect(); } catch (_) {}
        });
        oscillatorsRef.current = [];
      }, fade ? 1000 : 0);
      setPlaying(false);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    return () => {
      stopAudio(false);
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-start audio with a slight delay (audio begins before the visual fully completes)
  useEffect(() => {
    const t = setTimeout(() => startAudio(), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timer
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [playing]);

  // Whisper rotation: appear 4s, fade 2s, gap 7s.
  // Stay On uses a slower, deeper cycle (longer reveal, longer pause) for a
  // more evolving ambient feel versus Reset Mode's snappier cadence.
  useEffect(() => {
    const whispers = resetMode ? (recipe.text || WHISPERS_FALLBACK) : (recipe.stayOnText || recipe.text || WHISPERS_FALLBACK);
    let active = true;
    setWhisperIdx(0);
    const visibleMs = resetMode ? 7000 : 11000;
    const gapMs = resetMode ? 5000 : 8000;
    const cycle = async () => {
      while (active) {
        setWhisperVisible(true);
        await new Promise((r) => setTimeout(r, visibleMs));
        setWhisperVisible(false);
        await new Promise((r) => setTimeout(r, gapMs));
        if (!active) break;
        setWhisperIdx((i) => (i + 1) % whispers.length);
      }
    };
    cycle();
    return () => {
      active = false;
    };
  }, [recipe, resetMode]);

  const togglePlay = () => {
    if (playing) stopAudio(true);
    else startAudio();
  };

  const handleExit = () => {
    stopAudio(true);
    setTimeout(() => navigate("/practices/state-tuning/select"), 400);
  };

  const whispers = resetMode ? (recipe.text || WHISPERS_FALLBACK) : (recipe.stayOnText || recipe.text || WHISPERS_FALLBACK);
  const dim = resetMode ? 0.55 : 1.0;
  // Reset Mode → short, self-completing session (3–4 min). Ends with a close-out.
  // Stay On    → open ambient field; runs until the user exits. No auto-end.
  const ended = resetMode && elapsed >= recipe.resetDuration;

  return (
    <div
      className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center"
      style={{
        background: "radial-gradient(ellipse at 50% 50%, #08111F 0%, #050A14 50%, #02050B 100%)",
      }}
      data-testid="state-tuning-play"
    >
      {/* Subtle starfield */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.4' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />
      {/* Reset-Mode dim layer */}
      <motion.div
        aria-hidden
        animate={{ opacity: resetMode ? 0.32 : 0 }}
        transition={{ duration: 1.4 }}
        className="pointer-events-none absolute inset-0 bg-black"
      />

      {/* Stay-On evolving ambient layer — slow breathing color shift.
          Only visible in Stay On to make the mode feel audibly/visually distinct. */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        animate={{
          opacity: resetMode ? 0 : [0.0, 0.35, 0.1, 0.30, 0.0],
        }}
        transition={{ duration: resetMode ? 0.8 : 42, repeat: resetMode ? 0 : Infinity, ease: "easeInOut" }}
        style={{
          background: recipe.hue === "warm"
            ? "radial-gradient(ellipse 100% 70% at 50% 50%, rgba(225,180,140,0.14) 0%, transparent 65%)"
            : recipe.hue === "cool"
              ? "radial-gradient(ellipse 100% 70% at 50% 50%, rgba(170,195,225,0.14) 0%, transparent 65%)"
              : "radial-gradient(ellipse 100% 70% at 50% 50%, rgba(210,215,230,0.12) 0%, transparent 65%)",
        }}
      />

      {/* Exit */}
      <button
        onClick={handleExit}
        className="absolute top-5 left-5 p-2 rounded-full hover:bg-white/[0.05] transition-colors z-20"
        aria-label="Exit"
        data-testid="state-tuning-play-exit"
      >
        <X size={20} className="text-white/45" />
      </button>

      {/* The orb */}
      <div className="relative z-10 flex-1 flex items-center justify-center w-full" style={{ opacity: dim }}>
        <BreathingOrb size={300} hue={recipe.hue} intensity={resetMode ? 0.7 : 1} audioPulse={playing ? 0.5 : 0} />
      </div>

      {/* Whisper text */}
      <div className="relative z-10 h-14 flex items-center justify-center" style={{ opacity: dim }}>
        <AnimatePresence mode="wait">
          {whisperVisible && !ended && (
            <motion.p
              key={whisperIdx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.78, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 1.6, ease: "easeOut" }}
              className="text-white/85 text-lg italic"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
            >
              {whispers[whisperIdx]}
            </motion.p>
          )}
          {ended && (
            <motion.div key="end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4 }} className="text-center">
              <p className="text-white/85 text-xl italic mb-1" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
                Stay with this…
              </p>
              <p className="text-white/45 text-sm">or gently return</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* End screen buttons */}
      {ended && (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.6 }}
          className="relative z-10 mt-8 flex gap-4"
        >
          <button
            onClick={() => { setElapsed(0); if (!playing) startAudio(); }}
            className="px-6 py-2.5 rounded-full text-white/85 text-xs tracking-[0.2em] uppercase border border-white/15 hover:bg-white/[0.04] transition-colors"
            data-testid="state-tuning-continue"
          >
            Continue
          </button>
          <button
            onClick={handleExit}
            className="px-6 py-2.5 rounded-full text-white/55 text-xs tracking-[0.2em] uppercase hover:text-white/80 transition-colors"
            data-testid="state-tuning-return"
          >
            Return
          </button>
        </motion.div>
      )}

      {/* Footer controls */}
      {!ended && (
        <div className="relative z-10 pb-10 pt-6 w-full flex flex-col items-center gap-6">
          <button
            onClick={togglePlay}
            className="w-12 h-12 rounded-full flex items-center justify-center hover:bg-white/[0.05] transition-colors"
            aria-label={playing ? "Pause" : "Play"}
            data-testid="state-tuning-play-toggle"
          >
            {playing ? <Pause size={18} className="text-white/55" /> : <Play size={18} className="text-white/65" fill="currentColor" />}
          </button>

          {/* Mode toggle pill */}
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 rounded-full px-1 py-1 backdrop-blur-md"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
              data-testid="state-tuning-mode-toggle"
            >
              <button
                onClick={() => setResetMode(true)}
                className={`px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase rounded-full transition-all ${resetMode ? "text-white/85 bg-white/[0.06]" : "text-white/40"}`}
                data-testid="state-tuning-reset-btn"
              >
                Reset Mode
              </button>
              <button
                onClick={() => setResetMode(false)}
                className={`px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase rounded-full transition-all ${!resetMode ? "text-white/85 bg-white/[0.06]" : "text-white/40"}`}
                data-testid="state-tuning-stay-btn"
              >
                Stay On
              </button>
            </div>
            <p className="text-white/40 text-[10.5px] tracking-[0.22em] uppercase" data-testid="state-tuning-mode-hint">
              {resetMode
                ? `Reset · ~${Math.round(recipe.resetDuration / 60)} min · self-closes`
                : `Stay On · open ambient · runs until you stop`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export { BreathingOrb };
export default StateTuningPlay;
